<script setup lang="ts">
/**
 * Fair Home redesign shell — Track H
 * Figma RedisignHomePage · nodes `15:*`
 * H11 YSL pager: Swiper vertical + creative + mousewheel
 * H10 Persona ✅ · H13 Search drawer ✅
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { STYLE_SPACE_ROUTE } from '@/router/routes'
import { Mousewheel, Keyboard, EffectCreative } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import type { Swiper as SwiperInstance } from 'swiper/types'
import { useFairPageLoaderReady } from '@/features/page-loader/useFairPageLoaderReady'
import { FairSiteHeader } from '@/ui/patterns/FairSiteHeader'
import {
  resolveFairSiteHeaderSize,
  type FairSiteHeaderAction,
} from '@/ui/tokens/fair-site-header.data'
import FairHomeV2AiBlock from './FairHomeV2AiBlock.vue'
import FairHomeV2Capsules from './FairHomeV2Capsules.vue'
import FairHomeV2Hero from './FairHomeV2Hero.vue'
import FairHomeV2Selected from './FairHomeV2Selected.vue'
import FairHomeV2Footer from './FairHomeV2Footer.vue'
import FairHomeV2Popular from './FairHomeV2Popular.vue'
import FairHomeV2MenuDrawer from './FairHomeV2MenuDrawer.vue'
import FairHomeV2SearchDrawer from './FairHomeV2SearchDrawer.vue'
import FairHomeV2Services from './FairHomeV2Services.vue'
import {
  FAIR_HOME_V2_DESKTOP_MIN_WIDTH,
  FAIR_HOME_V2_SECTIONS,
  isFairHomeV2Compact,
} from './fair-home-v2.data'
import {
  FAIR_HOME_V2_PAGER_CREATIVE_EFFECT,
  FAIR_HOME_V2_PAGER_MOUSEWHEEL,
  FAIR_HOME_V2_PAGER_SPEED_MS,
} from './fair-home-v2-scroll.data'
import { useFairHomeV2Persona } from './useFairHomeV2Persona'
import { useFairHomeV2Scroll } from './useFairHomeV2Scroll'
import 'swiper/css'
import 'swiper/css/effect-creative'
import './fair-home-v2.css'

const viewportWidth = ref(
  typeof window !== 'undefined' ? window.innerWidth : FAIR_HOME_V2_DESKTOP_MIN_WIDTH,
)

const isCompact = computed(() => isFairHomeV2Compact(viewportWidth.value))
const headerSize = computed(() => resolveFairSiteHeaderSize(viewportWidth.value))

const audience = ref('women')
const router = useRouter()
const searchOpen = ref(false)
const menuOpen = ref(false)
const activeSlide = ref(0)
const swiperRef = ref<SwiperInstance | null>(null)

const { persona } = useFairHomeV2Persona()
const { reducedMotion } = useFairHomeV2Scroll()

/**
 * Hold the branded loader until the hero rail is fully dressed. It is the heaviest thing
 * on the first screen (~600 KB of WebP fetched by a runtime that lives outside the bundle),
 * and a half-loaded rack — bare rail, garments popping in one by one — is exactly what the
 * overlay exists to hide. Only one FairHomeV2Hero is mounted at a time (compact/desktop
 * branch), so a single flag is enough. If the rail never reports in, the store's
 * FAIR_PAGE_LOADER_MAX_MS cap dismisses the overlay anyway.
 */
const heroRailLoaded = ref(false)

function handleHeroRailLoad() {
  heroRailLoaded.value = true
}

useFairPageLoaderReady(heroRailLoaded)

const pagerModules = [Mousewheel, Keyboard, EffectCreative]

const creativeEffect = {
  limitProgress: FAIR_HOME_V2_PAGER_CREATIVE_EFFECT.limitProgress,
  shadowPerProgress: FAIR_HOME_V2_PAGER_CREATIVE_EFFECT.shadowPerProgress,
  progressMultiplier: FAIR_HOME_V2_PAGER_CREATIVE_EFFECT.progressMultiplier,
  perspective: FAIR_HOME_V2_PAGER_CREATIVE_EFFECT.perspective,
  prev: { ...FAIR_HOME_V2_PAGER_CREATIVE_EFFECT.prev },
  next: { ...FAIR_HOME_V2_PAGER_CREATIVE_EFFECT.next },
}

function onResize() {
  viewportWidth.value = window.innerWidth
  swiperRef.value?.update()
}

onMounted(() => {
  window.addEventListener('resize', onResize, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

function handleHeaderAction(action: FairSiteHeaderAction) {
  if (action.id === 'search') {
    searchOpen.value = true
  } else if (action.id === 'style-space') {
    void router.push(STYLE_SPACE_ROUTE)
  }
}

function handleMenuClick() {
  menuOpen.value = true
}

function handleSwiper(swiper: SwiperInstance) {
  swiperRef.value = swiper
}

function handleSlideChange(swiper: SwiperInstance) {
  activeSlide.value = swiper.activeIndex
}

/** Footer slide is dark edge-to-edge — the fixed header would sit on top of
 *  its link columns, so it fades out while the footer is active. */
const headerHidden = computed(
  () =>
    !reducedMotion.value
    && FAIR_HOME_V2_SECTIONS[activeSlide.value]?.id === 'footer',
)

function sectionFigmaNode(
  section: (typeof FAIR_HOME_V2_SECTIONS)[number],
): string {
  return isCompact.value ? section.figmaCompact : section.figmaDesktop
}

function slideClass(sectionId: string): Record<string, boolean> {
  return {
    'fair-home-v2__slide': true,
    'fair-home-v2__slide--hero': sectionId === 'hero',
    'fair-home-v2__slide--footer': sectionId === 'footer',
  }
}

function isHeroSection(sectionId: string): boolean {
  return sectionId === 'hero'
}

function isCapsulesSection(sectionId: string): boolean {
  return sectionId === 'capsules'
}

function isServicesSection(sectionId: string): boolean {
  return sectionId === 'services'
}

function isSelectedSection(sectionId: string): boolean {
  return sectionId === 'selected'
}

function isAiSection(sectionId: string): boolean {
  return sectionId === 'ai'
}

function isPopularSection(sectionId: string): boolean {
  return sectionId === 'popular'
}

function isFooterSection(sectionId: string): boolean {
  return sectionId === 'footer'
}
</script>

<template>
  <div
    class="fair-home-v2"
    :class="{
      'fair-home-v2--compact': isCompact,
      'fair-home-v2--pager': !reducedMotion,
      'fair-home-v2--static': reducedMotion,
    }"
    data-unit="H11"
    data-scroll="ysl-swiper"
  >
    <FairSiteHeader
      v-model:audience="audience"
      class="fair-home-v2__header"
      :class="{ 'fair-home-v2__header--hidden': headerHidden }"
      :aria-hidden="headerHidden ? 'true' : undefined"
      :inert="headerHidden || undefined"
      :size="headerSize"
      @menu-click="handleMenuClick"
      @action-click="handleHeaderAction"
    />

    <!-- prefers-reduced-motion: normal document stack, no wheel pager -->
    <main
      v-if="reducedMotion"
      class="fair-home-v2__main fair-home-v2__main--static"
    >
      <template
        v-for="section in FAIR_HOME_V2_SECTIONS"
        :key="section.id"
      >
        <FairHomeV2Hero
          v-if="isHeroSection(section.id)"
          :compact="isCompact"
          :data-unit="section.unit"
          :data-figma="sectionFigmaNode(section)"
          @rail-load="handleHeroRailLoad"
        />
        <FairHomeV2Capsules
          v-else-if="isCapsulesSection(section.id)"
          :compact="isCompact"
          :audience="audience"
          :viewport-width="viewportWidth"
          :data-unit="section.unit"
          :data-figma="sectionFigmaNode(section)"
        />
        <FairHomeV2Services
          v-else-if="isServicesSection(section.id)"
          :compact="isCompact"
          :data-unit="section.unit"
          :data-figma="sectionFigmaNode(section)"
        />
        <FairHomeV2Selected
          v-else-if="isSelectedSection(section.id)"
          :compact="isCompact"
          :audience="audience"
          :viewport-width="viewportWidth"
          :persona="persona.selected"
          :data-unit="section.unit"
          :data-figma="sectionFigmaNode(section)"
        />
        <FairHomeV2AiBlock
          v-else-if="isAiSection(section.id)"
          :compact="isCompact"
          :persona="persona.ai"
          :completeness-percent="persona.score"
          :data-unit="section.unit"
          :data-figma="sectionFigmaNode(section)"
        />
        <FairHomeV2Popular
          v-else-if="isPopularSection(section.id)"
          :compact="isCompact"
          :audience="audience"
          :data-unit="section.unit"
          :data-figma="sectionFigmaNode(section)"
        />
        <FairHomeV2Footer
          v-else-if="isFooterSection(section.id)"
          :compact="isCompact"
          :data-unit="section.unit"
          :data-figma="sectionFigmaNode(section)"
        />
      </template>
    </main>

    <!-- YSL parity: vertical creative Swiper -->
    <Swiper
      v-else
      class="fair-home-v2__pager"
      direction="vertical"
      :modules="pagerModules"
      :slides-per-view="1"
      :space-between="0"
      :speed="FAIR_HOME_V2_PAGER_SPEED_MS"
      :effect="'creative'"
      :creative-effect="creativeEffect"
      :watch-slides-progress="true"
      :mousewheel="FAIR_HOME_V2_PAGER_MOUSEWHEEL"
      :keyboard="{ enabled: true, onlyInViewport: true, pageUpDown: true }"
      :resistance-ratio="0.85"
      :grab-cursor="false"
      :nested="false"
      :simulate-touch="true"
      :touch-release-on-edges="false"
      :aria-label="'Home sections'"
      @swiper="handleSwiper"
      @slide-change="handleSlideChange"
    >
      <SwiperSlide
        v-for="section in FAIR_HOME_V2_SECTIONS"
        :key="section.id"
        :class="slideClass(section.id)"
        :data-section="section.id"
        :data-unit="section.unit"
        :data-figma="sectionFigmaNode(section)"
      >
        <div class="fair-home-v2__slide-inner">
          <FairHomeV2Hero
            v-if="isHeroSection(section.id)"
            :compact="isCompact"
            @rail-load="handleHeroRailLoad"
          />
          <FairHomeV2Capsules
            v-else-if="isCapsulesSection(section.id)"
            :compact="isCompact"
            :audience="audience"
            :viewport-width="viewportWidth"
          />
          <FairHomeV2Services
            v-else-if="isServicesSection(section.id)"
            :compact="isCompact"
          />
          <FairHomeV2Selected
            v-else-if="isSelectedSection(section.id)"
            :compact="isCompact"
            :audience="audience"
            :viewport-width="viewportWidth"
            :persona="persona.selected"
          />
          <FairHomeV2AiBlock
            v-else-if="isAiSection(section.id)"
            :compact="isCompact"
            :persona="persona.ai"
            :completeness-percent="persona.score"
          />
          <FairHomeV2Popular
            v-else-if="isPopularSection(section.id)"
            :compact="isCompact"
            :audience="audience"
          />
          <FairHomeV2Footer
            v-else-if="isFooterSection(section.id)"
            :compact="isCompact"
          />
        </div>
      </SwiperSlide>
    </Swiper>

    <FairHomeV2SearchDrawer
      v-model:open="searchOpen"
      :compact="isCompact"
      :audience="audience"
    />

    <FairHomeV2MenuDrawer
      v-model:open="menuOpen"
      v-model:audience="audience"
      :compact="isCompact"
    />
  </div>
</template>
